import {
  createRestyleComponent,
  VariantProps,
  createVariant,
} from "@shopify/restyle";
import {
  Platform,
  TextInput,
  TextInputProps,
  useColorScheme,
} from "react-native";
import { Theme, fonts, theme } from "@theme";
import LText from "@components/LText";
import LBox from "@components/LBox";
import { BottomSheetTextInput } from "@gorhom/bottom-sheet";
import type { TextInput as TextInputType } from "react-native-gesture-handler";
import React, { forwardRef } from "react";

const CustomTextInput = forwardRef<
  TextInputType,
  VariantProps<Theme, "inputVariants"> &
    TextInputProps & { bottomSheet?: boolean }
>(function CustomTextInputComp(props, ref) {
  const colorScheme = useColorScheme();
  // `BottomSheetTextInput` is not web-compatible: it relies on undocumented
  // React Native internals (`TextInput.State.currentlyFocusedInput`) that do not
  // exist in react-native-web, which breaks tabbing and sheet dismissal.
  if (props.bottomSheet && Platform.OS !== "web") {
    // `BottomSheetTextInput`'s ref type doesn't match RNGH TextInput's ref cleanly.
    return (
      <BottomSheetTextInput
        {...props}
        allowFontScaling={false}
        maxFontSizeMultiplier={1}
        ref={ref as unknown as never}
      />
    );
  }

  return (
    <TextInput
      keyboardAppearance={
        colorScheme === "light" || colorScheme === "dark"
          ? colorScheme
          : undefined
      }
      ref={ref}
      {...props}
      allowFontScaling={false}
      maxFontSizeMultiplier={1}
    />
  );
});

const LInput = createRestyleComponent<
  VariantProps<Theme, "inputVariants"> &
    TextInputProps & { bottomSheet?: boolean },
  Theme
>(
  [createVariant({ themeKey: "inputVariants" })],
  // Work around React type duplication in this repo/tooling setup.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  CustomTextInput as any,
);

//get the props of LInput and add error prop
type LInputProps = React.ComponentProps<typeof LInput>;

export const FormInput = ({
  error,
  style,
  ...rest
}: LInputProps & { error?: string }) => {
  return (
    <>
      <LInput
        {...rest}
        returnKeyType="done"
        variant="searchBarSmall"
        style={[
          {
            backgroundColor: theme.colors.experienceBorder,
            borderRadius: theme.borderRadii["s-m"],
            color: theme.colors.white,
            fontFamily: fonts.semiBold,
            fontSize: 16,
            lineHeight: 19,
            minHeight: 46,
            paddingHorizontal: theme.spacing.s3,
          },
          style,
        ]}
      />
      {error && (
        <LBox
          position="absolute"
          bottom={theme.spacing.s1}
          right={theme.spacing.s2}
        >
          <LText variant="bodyS2" color="playerQueueBg1">
            {error}
          </LText>
        </LBox>
      )}
    </>
  );
};

export default LInput;
